import { useState, useRef } from "react";
import { KeyRound, Camera } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export default function PerfilStaff() {
    const { user } = useAuth();
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const fileRef = useRef();

    async function changePassword() {
        if (!form.current_password || !form.new_password || !form.confirm_password) {
            toast.error("Completá todos los campos");
            return;
        }
        if (form.new_password !== form.confirm_password) {
            toast.error("Las contraseñas nuevas no coinciden");
            return;
        }
        if (form.new_password.length < 6) {
            toast.error("La contraseña debe tener al menos 6 caracteres");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Contraseña actualizada");
            setForm({ current_password: "", new_password: "", confirm_password: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al cambiar la contraseña");
        } finally {
            setSaving(false);
        }
    }

    async function uploadPhoto(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingPhoto(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await api.patch(`/users/${user.id}`, { profile_photo_id: data.id });
            toast.success("Foto actualizada");
        } catch (e) {
            toast.error("Error al subir la foto");
        } finally {
            setUploadingPhoto(false);
        }
    }

    const roleLabel = {
        profesor: "Profesor",
        psicologo: "Psicólogo",
        administrativo: "Administrativo",
    };

    return (
        <div className="p-6 md:p-10 fade-in">
            <h1 className="text-3xl font-semibold mb-1">Mi Perfil</h1>
            <p className="text-edu-body mb-8">Administrá tu información personal.</p>

            {/* Foto y datos */}
            <div className="edu-card p-6 mb-6 max-w-md mx-auto">
                <h2 className="text-lg font-semibold mb-4">Mis datos</h2>

                {/* Foto de perfil */}
                <div className="flex flex-col items-center mb-5">
                    <div className="relative">
                        <div className="w-20 h-20 rounded-full bg-edu-navy/10 flex items-center justify-center overflow-hidden border-2 border-edu">
                            {user?.profile_photo_id ? (
                                <img
                                    src={`/api/files/${user.profile_photo_id}/download`}
                                    alt="Foto de perfil"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <span className="text-2xl font-bold text-edu-navy">
                                    {user?.name?.charAt(0).toUpperCase()}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => fileRef.current.click()}
                            disabled={uploadingPhoto}
                            className="absolute bottom-0 right-0 p-1.5 rounded-full bg-edu-navy text-white hover:opacity-90"
                        >
                            <Camera size={12} />
                        </button>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                    </div>
                    <p className="text-xs text-edu-muted mt-2">
                        {uploadingPhoto ? "Subiendo..." : "Click en el ícono para cambiar foto"}
                    </p>
                </div>

                <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-edu-muted">Nombre</span>
                        <span className="font-medium">{user?.name}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-edu-muted">Email</span>
                        <span className="font-medium">{user?.email}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-edu-muted">Rol</span>
                        <span className="font-medium">{roleLabel[user?.role] || user?.role}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-edu-muted">Colegio</span>
                        <span className="font-medium">{user?.school || "—"}</span>
                    </div>
                </div>
            </div>

            {/* Cambio de contraseña */}
            <div className="edu-card p-6 max-w-md mx-auto">
                <h2 className="text-lg font-semibold mb-4 inline-flex items-center gap-2">
                    <KeyRound size={18} /> Cambiar contraseña
                </h2>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">
                            Contraseña actual
                        </span>
                        <input
                            type="password"
                            value={form.current_password}
                            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                            className="edu-input w-full"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">
                            Nueva contraseña
                        </span>
                        <input
                            type="password"
                            value={form.new_password}
                            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                            className="edu-input w-full"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">
                            Confirmar nueva contraseña
                        </span>
                        <input
                            type="password"
                            value={form.confirm_password}
                            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                            className="edu-input w-full"
                        />
                    </label>
                    <button
                        onClick={changePassword}
                        disabled={saving}
                        className="edu-btn-primary px-4 py-2 text-sm w-full mt-2"
                    >
                        {saving ? "Guardando…" : "Cambiar contraseña"}
                    </button>
                </div>
            </div>
        </div>
    );
}
